import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { apiErrorMessage } from "../API";
import { askCall, getCallMedia, getCallPage, prepareCallPage, renameCall } from "./api";
import { CallPageView } from "./CallViews";
import { rememberCallAccess } from "./listQuery";
import { downloadCall } from "./downloadCall";
import { WRITEUP_POLL_MS, WRITEUP_POLL_TRIES } from "./processing";
import type { CallPageData } from "./types";
import "./calls.css";

export default function CallPage() {
  const { botId = "" } = useParams();
  const [page, setPage] = useState<CallPageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = () => {
    setError(null);
    getCallPage(botId)
      .then(setPage)
      .catch((reason: unknown) => setError(apiErrorMessage(reason, "Could not load this call.")));
  };

  useEffect(() => {
    rememberCallAccess(botId);
    load();
    // Reloading when the id changes is the whole effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  const seek = (seconds: number) => {
    const media = document.querySelector<HTMLMediaElement>(
      ".dg-call-stage video, .dg-call-audio",
    );
    if (!media) return;
    // A click on a silent stretch is still a place in the recording.
    // Leave a paused recording paused; only a playing one keeps going.
    const resume = !media.paused;
    const length = media.duration;
    media.currentTime = Number.isFinite(length) ? Math.min(seconds, length) : seconds;
    if (resume) media.play().catch(() => undefined);
  };

  const prepare = useCallback(async () => {
    // The write-up is a card the server computes after the first open, so the
    // first answer is usually "not ready": ask again until it lands.
    for (let attempt = 0; ; attempt += 1) {
      const next = await prepareCallPage(botId);
      if (!mounted.current) return;
      setPage((current) =>
        current
          ? {
              ...current,
              brief: next.brief,
              summary: next.summary,
              action_points: next.action_points,
              topics: next.topics,
              screen_shares: next.screen_shares,
              writeup_ready: next.writeup_ready,
              widgets: next.widgets ?? current.widgets,
              call: { ...current.call, title: next.title },
            }
          : current,
      );
      if (next.writeup_ready) return;
      if (attempt >= WRITEUP_POLL_TRIES) {
        throw new Error("The write-up is taking longer than expected.");
      }
      await new Promise((resolve) => window.setTimeout(resolve, WRITEUP_POLL_MS));
    }
  }, [botId]);

  const rename = useCallback(
    async (title: string) => {
      const next = await renameCall(botId, title);
      setPage((current) =>
        current ? { ...current, call: { ...current.call, title: next } } : current,
      );
      return next;
    },
    [botId],
  );

  if (error) {
    return (
      <p className="dg-call-note">
        {error} <Link to="/calls">Back to calls</Link>
      </p>
    );
  }
  if (!page) return <p className="dg-call-note">Loading…</p>;

  return (
    <CallPageView
      page={page}
      onSeek={seek}
      onRefresh={load}
      onRefreshMedia={async () => {
        const media = await getCallMedia(botId);
        setPage((current) => (current ? { ...current, media } : current));
        return media;
      }}
      onAsk={async (question) => {
        try {
          return await askCall(botId, question);
        } catch (reason) {
          throw new Error(apiErrorMessage(reason, "Could not answer that."));
        }
      }}
      onPrepare={async () => {
        try {
          await prepare();
        } catch (reason) {
          throw new Error(apiErrorMessage(reason, "Could not prepare this call."));
        }
      }}
      onRename={async (title) => {
        try {
          return await rename(title);
        } catch (reason) {
          throw new Error(apiErrorMessage(reason, "Could not rename this call."));
        }
      }}
      onSave={(onStarted) => downloadCall(botId, onStarted)}
    />
  );
}
